import { ArrowLeft, Pencil, Receipt, Trash2 } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useExpenseEditor } from '@/features/expenses/editor';
import { useActiveGroup } from '@/features/groups/store';
import { useLedgerStore, useMemberLookup } from '@/features/ledger/store';
import { formatDate } from '@/lib/date';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  CategoryGlyph,
  DataTable,
  EmptyState,
  Money,
  PageHeader,
  confirm,
  toast,
  type Column,
} from '@/ui';
import { formatMoney } from '@domain/money';
import { SPLIT_TYPE_LABELS, type ExpenseParticipant } from '@domain/types';

export function ExpenseDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const openEdit = useExpenseEditor((state) => state.openEdit);

  const group = useActiveGroup();
  const expense = useLedgerStore((state) =>
    state.allExpenses.find((candidate) => candidate.id === id)
  );
  const removeExpense = useLedgerStore((state) => state.removeExpense);
  const memberLookup = useMemberLookup();

  const currency = group?.currency ?? 'USD';

  async function handleDelete() {
    if (!expense) return;

    const ok = await confirm({
      title: `Delete "${expense.title}"?`,
      message: 'Balances update immediately. This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    await removeExpense(expense.id);
    toast.success('Expense deleted.');
    navigate('/expenses');
  }

  if (!expense) {
    return (
      <>
        <PageHeader title="Expense" />
        <Card padded={false}>
          <EmptyState
            icon={Receipt}
            title="Expense not found"
            description="It may have been deleted."
            action={<Button onClick={() => navigate('/expenses')}>Back to expenses</Button>}
          />
        </Card>
      </>
    );
  }

  const columns: Column<ExpenseParticipant>[] = [
    {
      key: 'member',
      header: 'Member',
      cell: (row) => {
        const member = memberLookup.get(row.memberId);
        const name = member?.name ?? 'Removed member';
        return (
          <span className="flex items-center gap-2.5">
            <Avatar name={name} id={row.memberId} size="sm" dimmed={!member?.isActive} />
            <span className="truncate font-medium">{name}</span>
          </span>
        );
      },
    },
    {
      key: 'weight',
      header: expense.splitType === 'shares' ? 'Shares' : 'Basis',
      align: 'right',
      hideOnNarrow: true,
      cell: (row) =>
        row.weight != null ? (
          <span className="tnum text-muted">
            {expense.splitType === 'percentage' ? `${row.weight}%` : row.weight}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'percent',
      header: 'Of total',
      align: 'right',
      hideOnNarrow: true,
      cell: (row) => (
        <span className="tnum text-muted">
          {expense.amountMinor > 0
            ? `${Math.round((row.shareAmountMinor / expense.amountMinor) * 1000) / 10}%`
            : '—'}
        </span>
      ),
    },
    {
      key: 'share',
      header: 'Owes',
      align: 'right',
      width: '140px',
      cell: (row) => (
        <Money minor={row.shareAmountMinor} currency={currency} className="font-semibold" />
      ),
    },
  ];

  return (
    <>
      <Link
        to="/expenses"
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-muted transition hover:text-fg"
      >
        <ArrowLeft size={14} /> All expenses
      </Link>

      <PageHeader
        title={expense.title}
        description={`${formatDate(expense.date)} · paid by ${expense.paidByName}`}
        actions={
          <>
            <Button variant="secondary" icon={Pencil} onClick={() => openEdit(expense.id)}>
              Edit
            </Button>
            <Button variant="danger" icon={Trash2} onClick={handleDelete}>
              Delete
            </Button>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <Card>
          <div className="flex items-center gap-3">
            <CategoryGlyph icon={expense.categoryIcon} color={expense.categoryColor} size="lg" />
            <div className="min-w-0">
              <p className="tnum text-2xl font-semibold">
                {formatMoney(expense.amountMinor, currency)}
              </p>
              <p className="truncate text-[13px] text-muted">
                {expense.categoryName ?? 'Uncategorised'}
              </p>
            </div>
          </div>

          <dl className="mt-5 flex flex-col gap-3 text-[13px]">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted">Split method</dt>
              <dd>
                <Badge tone="primary">{SPLIT_TYPE_LABELS[expense.splitType]}</Badge>
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted">Participants</dt>
              <dd>{expense.participants.length}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted">Date</dt>
              <dd>{formatDate(expense.date)}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted">Paid by</dt>
              <dd className="flex items-center gap-1.5">
                <Avatar name={expense.paidByName} id={expense.paidBy} size="xs" />
                {expense.paidByName}
              </dd>
            </div>
          </dl>

          {expense.notes && (
            <div className="mt-5 border-t border-line pt-4">
              <p className="text-[12px] font-medium uppercase tracking-wide text-muted">Notes</p>
              <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed">
                {expense.notes}
              </p>
            </div>
          )}
        </Card>

        <section>
          <CardHeader
            title="Who owes what"
            description="Shares always add up to the exact total — rounding is distributed, never dropped."
          />
          <DataTable
            columns={columns}
            rows={expense.participants}
            rowKey={(row) => row.id}
            footer={
              <>
                <td className="px-4 py-2.5 text-[13px] font-medium">Total</td>
                <td className="hidden lg:table-cell" />
                <td className="hidden lg:table-cell" />
                <td className="px-4 py-2.5 text-right">
                  <Money
                    minor={expense.participants.reduce(
                      (total, participant) => total + participant.shareAmountMinor,
                      0
                    )}
                    currency={currency}
                    className="font-semibold"
                  />
                </td>
              </>
            }
          />
        </section>
      </div>
    </>
  );
}
